import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Vault, Lock, Unlock, ShieldCheck, Trash2, Eye, Upload,
  Loader2, KeyRound, FileText, Clock, Share2, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useStepUp } from "../components/StepUpGuard";
import { PremiumUpsell } from "../components/pulse/FeedCards";
import { AppTopBar } from "../components/AppScreen";

/* Documents Locker — private copies of important documents behind PIN or
   fresh sign-in (step-up). Files are never shared, cached or indexed. */

const DOC_TYPES = [
  ["drivers_license", "Driver license"], ["auto_insurance", "Auto insurance"],
  ["passport", "Passport copy"], ["visa", "Visa copy"], ["medicare_card", "Medicare card"],
  ["health_insurance", "Health insurance"], ["id_card", "ID card"],
  ["birth_certificate", "Birth certificate"], ["other", "Other"],
];
const typeLabel = (v) => (DOC_TYPES.find(([k]) => k === v) || [null, "Other"])[1];

let lockerToken = ""; // in-memory only — never persisted
const hdrs = () => (lockerToken ? { "X-Locker-Token": lockerToken } : {});

export default function DocumentsLocker() {
  const guard = useStepUp();
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => api.get("/locker/status", { headers: hdrs() })
    .then((r) => setStatus(r.data)).catch(() => setStatus({ error: true })), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadFiles = useCallback(async () => {
    try {
      const r = await api.get("/locker/files", { headers: hdrs() });
      setFiles(r.data.items);
    } catch (e) {
      const code = e.response?.status;
      if (code === 401 && e.response?.data?.detail === "step_up_required") {
        guard(() => loadFiles()); // fresh sign-in (password / device Face ID via SSO)
      } else if (code === 423) {
        setFiles(null); // stays locked — PIN screen shows
      } else { toast.error("Could not open the locker"); }
    }
  }, [guard]);

  useEffect(() => {
    if (status && !status.error && (!status.pin_set || status.unlocked)) loadFiles();
  }, [status, loadFiles]);

  const unlock = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/locker/unlock", { pin: pinInput });
      lockerToken = r.data.token;
      setPinInput("");
      toast.success(`Unlocked for ${r.data.expires_in_minutes} minutes`);
      await loadStatus();
    } catch (er) { toast.error(er.response?.data?.detail || "Wrong PIN"); }
    setBusy(false);
  };

  const lockNow = async () => {
    await api.post("/locker/lock").catch(() => {});
    lockerToken = "";
    setFiles(null);
    loadStatus();
    toast.success("Locker locked");
  };

  if (!status) return <Shell><p className="mt-6 text-sm text-zinc-500">Opening locker…</p></Shell>;
  if (status.error) return <Shell><p className="mt-6 text-sm text-zinc-500">Locker unavailable — please try again.</p></Shell>;
  if (status.membership_required) return <Shell><SharedWithMe /><div className="mt-5"><PremiumUpsell feature="The Documents Locker" /></div></Shell>;

  const locked = status.pin_set && !status.unlocked;
  return (
    <Shell>
      <p className="text-zinc-600 mt-1 text-sm flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Private to you — protected by {status.pin_set ? "your locker PIN" : "a fresh sign-in (your device password or Face ID)"}. Every view is security-logged.</p>
      <SharedWithMe />

      {locked ? (
        <form onSubmit={unlock} className="mt-6 max-w-sm bg-white border border-zinc-200 rounded-2xl p-5 text-center" data-testid="locker-pin-form">
          <Lock className="w-8 h-8 text-blue-700 mx-auto" />
          <p className="mt-2 font-heading font-800">Enter your locker PIN</p>
          <input type="password" inputMode="numeric" autoComplete="off" value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="••••" data-testid="locker-pin-input"
            className="mt-3 w-40 mx-auto text-center tracking-[0.5em] text-xl border border-zinc-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-600" />
          <button type="submit" disabled={busy || pinInput.length < 4} data-testid="locker-unlock-btn"
            className="mt-3 w-full bg-blue-700 text-white font-700 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Unlock
          </button>
          <p className="mt-2 text-[11px] text-zinc-400">5 wrong attempts lock the locker for 15 minutes.</p>
        </form>
      ) : (
        <OpenLocker status={status} files={files} onChanged={loadFiles} onLock={lockNow} onPinChanged={loadStatus} />
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div data-testid="documents-locker">
      <AppTopBar title="Documents" />
      <div className="max-w-4xl mx-auto px-5 py-8">
      <Link to="/my-social-pulse?tab=life" className="text-sm text-blue-700 font-600 inline-flex items-center gap-1 hover:underline" data-testid="locker-back"><ArrowLeft className="w-4 h-4" /> My Social Pulse</Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight mt-2 flex items-center gap-3"><Vault className="w-8 h-8 text-blue-700" /> Documents Locker</h1>
      {children}
      </div>
    </div>
  );
}

function OpenLocker({ status, files, onChanged, onLock, onPinChanged }) {
  const guard = useStepUp();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ doc_type: "drivers_license", label: "", holder_name: "", expiry_date: "" });
  const [uploading, setUploading] = useState(false);
  const [pinDialog, setPinDialog] = useState(false);
  const [shareFile, setShareFile] = useState(null);
  const [shares, setShares] = useState([]);

  const loadShares = useCallback(() =>
    api.get("/locker/shares", { headers: hdrs() }).then((r) => setShares(r.data.items)).catch(() => {}), []);
  useEffect(() => { loadShares(); }, [loadShares]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a photo or PDF first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/locker/files", fd, { headers: hdrs() });
      toast.success("Stored securely");
      fileRef.current.value = "";
      setForm((x) => ({ ...x, label: "", holder_name: "", expiry_date: "" }));
      onChanged();
    } catch (e) {
      if (e.response?.data?.detail === "step_up_required") guard(upload);
      else toast.error(e.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const view = async (it) => {
    try {
      const r = await api.get(`/locker/files/${it.id}/content`, { headers: hdrs(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e.response?.status === 401) guard(() => view(it));
      else toast.error("Could not open the document");
    }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.label}" permanently?`)) return;
    try { await api.delete(`/locker/files/${it.id}`, { headers: hdrs() }); toast.success("Deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setPinDialog(true)} data-testid="locker-pin-manage"
          className="inline-flex items-center gap-1.5 text-xs font-700 border border-zinc-300 rounded-lg px-3 py-2 hover:border-blue-500">
          <KeyRound className="w-3.5 h-3.5" /> {status.pin_set ? "Change PIN" : "Add a locker PIN"}
        </button>
        {status.pin_set && (
          <button onClick={onLock} data-testid="locker-lock-btn"
            className="inline-flex items-center gap-1.5 text-xs font-700 border border-zinc-300 rounded-lg px-3 py-2 hover:border-rose-500">
            <Lock className="w-3.5 h-3.5" /> Lock now
          </button>
        )}
        {status.pin_set && <span className="text-[11px] text-zinc-400 self-center inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Auto-locks 10 min after unlock</span>}
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-2xl p-4" data-testid="locker-upload-form">
        <div className="text-sm font-800 font-heading flex items-center gap-2"><Upload className="w-4 h-4 text-blue-700" /> Add a document</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <select value={form.doc_type} onChange={(e) => setForm((f) => ({ ...f, doc_type: e.target.value }))}
            data-testid="locker-type-select" className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm bg-white">
            {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Label — e.g. Tom's license" data-testid="locker-label-input"
            className="flex-1 min-w-[160px] border border-zinc-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600" />
          <input value={form.holder_name} onChange={(e) => setForm((f) => ({ ...f, holder_name: e.target.value }))}
            placeholder="Holder (optional)" data-testid="locker-holder-input"
            className="w-36 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
          <input type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
            title="Expiry (optional)" data-testid="locker-expiry-input"
            className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm" />
          <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            data-testid="locker-file-input" className="text-sm py-2" />
          <button onClick={upload} disabled={uploading} data-testid="locker-upload-btn"
            className="inline-flex items-center gap-1.5 bg-blue-700 text-white px-5 py-2.5 rounded-lg font-700 hover:bg-blue-800 disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Store
          </button>
        </div>
        <p className="text-[11px] text-zinc-400 mt-2">JPG, PNG, WebP, HEIC or PDF. Stored encrypted in transit, never cached, never shared. Add an expiry date and we'll remind you 30 days before it lapses.</p>
      </div>

      {!files ? <p className="mt-5 text-sm text-zinc-500">Loading documents…</p> : files.length === 0 ? (
        <p className="mt-6 text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-xl" data-testid="locker-empty">Nothing stored yet — add your first document above.</p>
      ) : (
        <ul className="mt-5 space-y-2" data-testid="locker-file-list">
          {files.map((it) => {
            const fileShares = shares.filter((s) => s.file_id === it.id);
            return (
            <li key={it.id} className="bg-white border border-zinc-200 rounded-xl px-4 py-3" data-testid={`locker-file-${it.id}`}>
              <div className="flex flex-wrap items-center gap-3">
              <FileText className="w-5 h-5 text-blue-700 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-700">{it.label}</div>
                <div className="text-xs text-zinc-500">{typeLabel(it.doc_type)}{it.holder_name ? ` · ${it.holder_name}` : ""}{it.expiry_date ? ` · expires ${it.expiry_date}` : ""} · {(it.size / 1024).toFixed(0)} KB</div>
              </div>
              <span className="ml-auto flex items-center gap-1.5">
                <button onClick={() => view(it)} data-testid={`locker-view-${it.id}`}
                  className="inline-flex items-center gap-1 text-xs font-700 text-blue-700 border border-blue-200 bg-blue-50 rounded-lg px-2.5 py-1.5 hover:bg-blue-100"><Eye className="w-3.5 h-3.5" /> View</button>
                <button onClick={() => setShareFile(it)} data-testid={`locker-share-${it.id}`}
                  className="inline-flex items-center gap-1 text-xs font-700 text-violet-700 border border-violet-200 bg-violet-50 rounded-lg px-2.5 py-1.5 hover:bg-violet-100"><Share2 className="w-3.5 h-3.5" /> Share</button>
                <button onClick={() => del(it)} data-testid={`locker-del-${it.id}`} className="text-zinc-300 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
              </span>
              </div>
              {fileShares.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`locker-file-shares-${it.id}`}>
                  {fileShares.map((s) => (
                    <span key={s.id} className="inline-flex items-center gap-1.5 text-[11px] font-600 bg-violet-50 border border-violet-200 text-violet-800 rounded-full px-2.5 py-1">
                      <Share2 className="w-3 h-3" /> {s.to_name} · until {new Date(s.expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      <button title="Revoke" data-testid={`locker-share-revoke-${s.id}`}
                        onClick={() => api.delete(`/locker/shares/${s.id}`).then(() => { toast.success("Share revoked"); loadShares(); }).catch(() => toast.error("Could not revoke"))}
                        className="text-violet-400 hover:text-rose-600"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </li>
          );})}
        </ul>
      )}

      {shareFile && <ShareDialog file={shareFile} onClose={() => setShareFile(null)}
        onShared={() => { setShareFile(null); loadShares(); }} />}
      {pinDialog && <PinDialog hasPin={status.pin_set} onClose={() => setPinDialog(false)} onSaved={() => { setPinDialog(false); onPinChanged(); }} />}
    </div>
  );
}

/* Time-limited sharing with a verified family-space member. */
const SHARE_DURATIONS = [[4, "4 hours"], [24, "24 hours"], [72, "3 days"], [168, "7 days"]];

function ShareDialog({ file, onClose, onShared }) {
  const [family, setFamily] = useState(null);
  const [memberId, setMemberId] = useState("");
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/family").then((r) => setFamily(r.data)).catch(() => setFamily({ members: [] })); }, []);

  const options = family ? [
    ...(family.role === "member" ? [{ id: "owner", name: `${family.owner?.name || "Family organizer"} (organizer)` }] : []),
    ...family.members.filter((m) => m.status === "active").map((m) => ({ id: m.id, name: m.name })),
  ] : [];

  const share = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/locker/files/${file.id}/share`, { member_id: memberId, hours }, { headers: hdrs() });
      toast.success(`Shared with ${r.data.to_name} — access ends ${new Date(r.data.expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`);
      onShared();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not share"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="locker-share-dialog">
        <h3 className="font-heading font-800 text-lg flex items-center gap-2"><Share2 className="w-5 h-5 text-violet-700" /> Share "{file.label}"</h3>
        <p className="text-xs text-zinc-500 mt-1">Only verified family-space members can receive documents. Access ends automatically — you can also revoke it any time.</p>
        {!family ? <p className="mt-3 text-sm text-zinc-500">Loading family…</p> : options.length === 0 ? (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="locker-share-nofamily">
            No joined family members yet — send an invitation from My Social Pulse → Family first.
          </p>
        ) : (
          <>
            <select value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="locker-share-member"
              className="mt-3 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm bg-white">
              <option value="">Choose a family member…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="locker-share-hours"
              className="mt-2 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm bg-white">
              {SHARE_DURATIONS.map(([h, l]) => <option key={h} value={h}>Access for {l}</option>)}
            </select>
          </>
        )}
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-700 border border-zinc-300 rounded-lg" data-testid="locker-share-cancel">Cancel</button>
          <button onClick={share} disabled={busy || !memberId} data-testid="locker-share-confirm"
            className="px-4 py-2 text-sm font-700 bg-violet-700 text-white rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
          </button>
        </div>
      </div>
    </div>
  );
}

/* Documents a family member shared with me — viewable with my own sign-in,
   no PIN or premium needed (they're not my locker). */
function SharedWithMe() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/locker/shared-with-me").then((r) => setItems(r.data.items)).catch(() => {}); }, []);

  const view = async (it) => {
    try {
      const r = await api.get(`/locker/shared/${it.share_id}/content`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.status === 404 ? "This share has expired" : "Could not open the document"); }
  };

  if (items.length === 0) return null;
  return (
    <div className="mt-4 bg-violet-50 border border-violet-200 rounded-2xl p-4" data-testid="locker-shared-with-me">
      <div className="text-sm font-800 font-heading flex items-center gap-2"><Share2 className="w-4 h-4 text-violet-700" /> Shared with you</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it) => (
          <li key={it.share_id} className="flex flex-wrap items-center gap-3 bg-white border border-violet-200 rounded-xl px-3.5 py-2.5" data-testid={`locker-shared-${it.share_id}`}>
            <FileText className="w-4 h-4 text-violet-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-700">{it.label}</div>
              <div className="text-[11px] text-zinc-500">From {it.from_name} · {typeLabel(it.doc_type)} · access until {new Date(it.share_expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <button onClick={() => view(it)} data-testid={`locker-shared-view-${it.share_id}`}
              className="ml-auto inline-flex items-center gap-1 text-xs font-700 text-violet-700 border border-violet-300 bg-white rounded-lg px-2.5 py-1.5 hover:bg-violet-100"><Eye className="w-3.5 h-3.5" /> View</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PinDialog({ hasPin, onClose, onSaved }) {  const guard = useStepUp();
  const [oldPin, setOldPin] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/locker/pin", { pin, old_pin: oldPin || undefined });
      lockerToken = "";
      toast.success("PIN saved — locker will now require it");
      onSaved();
    } catch (e) {
      if (e.response?.data?.detail === "step_up_required") guard(save);
      else toast.error(e.response?.data?.detail || "Could not save PIN");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="locker-pin-dialog">
        <h3 className="font-heading font-800 text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-700" /> {hasPin ? "Change locker PIN" : "Set a locker PIN"}</h3>
        <p className="text-xs text-zinc-500 mt-1">4-8 digits. You'll enter it every time you open the locker. Changing it re-locks all sessions.</p>
        {hasPin && (
          <input type="password" inputMode="numeric" value={oldPin} onChange={(e) => setOldPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="Current PIN" data-testid="locker-oldpin-input"
            className="mt-3 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
        )}
        <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
          placeholder="New PIN (4-8 digits)" data-testid="locker-newpin-input"
          className="mt-2 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-700 border border-zinc-300 rounded-lg" data-testid="locker-pin-cancel">Cancel</button>
          <button onClick={save} disabled={busy || pin.length < 4} data-testid="locker-pin-save"
            className="px-4 py-2 text-sm font-700 bg-blue-700 text-white rounded-lg disabled:opacity-40">{busy ? "Saving…" : "Save PIN"}</button>
        </div>
      </div>
    </div>
  );
}
